import React, { Suspense, lazy, useRef } from 'react';
import { useLazyVisibility } from '../lib/performance';

// Lazy load the motion component to avoid including it in the main bundle if not needed immediately
const MotionDiv = lazy(() => import('motion/react').then(m => ({ default: m.motion.div })));

interface ScrollAnimateProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

/**
 * Wraps content in a lazy-loaded animation that only triggers when in viewport.
 * Reduces initial hydration cost and JS execution time.
 */
export function ScrollAnimate({ children, className = '', delay = 0 }: ScrollAnimateProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useLazyVisibility(ref, { 
    rootMargin: '100px', // Start loading 100px before entering
    threshold: 0.1 
  });

  return (
    <div ref={ref} className={className}>
      {isVisible ? (
        <Suspense fallback={<div className="opacity-0">{children}</div>}>
          <MotionDiv 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.6, 
              delay,
              ease: [0.21, 0.45, 0.32, 0.9]
            }}
          >
            {children}
          </MotionDiv>
        </Suspense>
      ) : (
        <div className="opacity-0">{children}</div>
      )}
    </div>
  );
}
